import { CircleInfoIcon } from "@xola/icons";
import { flash, Popover, SubmitButton, Switch, Tabs } from "@xola/ui-kit";
import { FormikHelpers, FormikValues } from "formik";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { GuideNotificationPreference, GuidePermission } from "types";
import { boolean, number, object } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { ConfirmLeaveModal } from "../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";

const validationPermissionSchema = object().shape({
    offset: number().min(0, "Number should be greater than or equal to 0"),
    modify: boolean(),
});

export const GlobalSettings = () => {
    const { section } = useParams<{ section?: string }>();
    const history = useHistory();

    const seller = useSeller();
    const {
        preferences: { guidePermission, guideNotification },
    } = seller;

    const initialSection = section ?? tabIds[0].id;
    const initialActiveIndex = tabIds.findIndex((tab) => tab.id === section ?? initialSection);
    const [activeTab, setActiveTab] = useState(initialActiveIndex === -1 ? 0 : initialActiveIndex);

    useEffect(() => {
        if (initialActiveIndex >= 0 && activeTab !== initialActiveIndex) {
            setActiveTab(initialActiveIndex);
        }
    }, [initialActiveIndex, activeTab]);

    const changeTab = (tabIndex: number) => {
        history.push(`/resources/guides/settings/${tabIds[tabIndex].id}`);
    };

    return (
        <div className="seller-body flex h-full flex-col border-l border-gray-lighter">
            <Tabs value={activeTab} onChange={changeTab}>
                <Tabs.Tab>Permissions</Tabs.Tab>
                <Tabs.Panel>
                    <PermissionsTab permission={guidePermission} />
                </Tabs.Panel>
                <Tabs.Tab>Email</Tabs.Tab>
                <Tabs.Panel>
                    <EmailTab notification={guideNotification} />
                </Tabs.Panel>
            </Tabs>
        </div>
    );
};

interface PermissionsTabProps {
    permission: GuidePermission;
}

interface PermissionsValues {
    modify: boolean;
    offset: number;
}

const PermissionsTab = ({ permission }: PermissionsTabProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const history = useHistory();
    const { canModifySchedules: modify, modifySchedulesOffset: offset } = permission;
    const initialState = { modify, offset };

    const { mutateAsync: updatePermissions, isLoading } = useMutation(async (data: GuidePermission) => {
        return api.post(`/sellers/${seller.id}/preferences/guidePermission`, data);
    });

    const handleSave = async (values: PermissionsValues, { setFieldValue }: FormikHelpers<PermissionsValues>) => {
        try {
            if (!values.modify) {
                // set offset to 0 in case when setting is turned off to prevent ConfirmLeaveModal popping after changes are saved
                setFieldValue("offset", 0);
            }

            await updatePermissions({
                canModifySchedules: values.modify,
                modifySchedulesOffset: values.modify ? values.offset : 1,
            });

            flash.success("Your settings are saved");
        } catch {}

        invalidate("sellers", seller.id);
    };

    const handleResetPermissions = (handleReset: () => void) => {
        handleReset();
        history.push("/resources/guides");
    };

    return (
        <ContentLayout isWide className="space-y-6">
            <XolaFormik
                validateOnChange
                validationSchema={validationPermissionSchema}
                initialValues={initialState}
                onSubmit={handleSave}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, errors, isValid }) => {
                    return (
                        <>
                            <ConfirmLeaveModal isFormTouched={!isEqual(initialState, values)} />
                            <Switch.Group>
                                <Switch
                                    isChecked={values.modify}
                                    size="medium"
                                    data-testid="global-settings-permissions-tab-allow-create-update-delete-schedules-toggle"
                                    onChange={(value: boolean) => {
                                        setFieldValue("modify", value);
                                    }}
                                />
                                <Switch.Label direction="right">
                                    <h4>Allow guides to create, update or delete schedules</h4>
                                </Switch.Label>
                            </Switch.Group>
                            {values.modify ? (
                                <div data-testid="global-settings-permissions-tab-prevent-guides-from-making-schedule-msg">
                                    Prevent guides from making schedule changes affecting their availability within
                                    <TimePickerPopover
                                        value={values.offset}
                                        isError={!!errors.offset}
                                        onApply={(value: number) => {
                                            setFieldValue("offset", value);
                                        }}
                                    />
                                    in advance
                                    <Popover delay={0} distance={10} duration={[300, 250]} skidding={50}>
                                        <CircleInfoIcon className="ml-2" />
                                        <Popover.Content className="space-y-2 p-4">
                                            <p className="p2 font-bold">For example:</p>
                                            <p className="p2">
                                                If you enter &rdquo;1 Week&rdquo;, the guide will not be able to make
                                                any changes to their schedule for the coming 7 days, calculated starting
                                                with the day that the attempted change is being made.
                                            </p>
                                            <p className="p2">
                                                They could however make a change that affects their schedule 8 days from
                                                the current date.
                                            </p>
                                        </Popover.Content>
                                    </Popover>
                                </div>
                            ) : null}
                            <ContentLayout.Footer>
                                <SubmitButton disabled={!isValid} isLoading={isLoading} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                                <SecondaryButton
                                    type="reset"
                                    disabled={isLoading}
                                    onClick={() => handleResetPermissions(handleReset)}
                                >
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

interface EmailTabProps {
    notification: GuideNotificationPreference;
}

const validationSchema = object().shape({
    offset: number().min(0, "Number should be greater than or equal to 0"),
    sellerOffset: number().min(0, "Number should be greater than or equal to 0"),
});

const EmailTab = ({ notification }: EmailTabProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();

    const {
        requireConfirmation,
        guideNotConfirmedToGuide: toGuide,
        guideNotConfirmedToSeller: toSeller,
        allowDecline,
    } = notification;
    const offset = toGuide?.offset;
    const sellerOffset = toSeller?.offset;
    const initialState = {
        open: requireConfirmation ?? false,
        allowDecline: allowDecline ?? true,
        guide: toGuide ? toGuide.enabled : false,
        offset,
        seller: toSeller ? toSeller.enabled : false,
        sellerOffset,
    };

    const { mutateAsync: updateEmailReminders, isLoading } = useMutation(async (data: any) => {
        return api.post(`/sellers/${seller.id}/preferences/guideNotification`, data);
    });

    const handleSave = async (values: FormikValues) => {
        const data = {
            includeGuideInfo: false,
            referGuideAs: "Guides",
            requireConfirmation: values.open,
            ...(values.open && { allowDecline: values.allowDecline }),
            guideNotAssigned: {
                enabled: false,
                offset: null,
                type: "guide_not_assigned",
            },
            guideNotConfirmedToGuide: {
                enabled: values.guide,
                offset: values.guide ? values.offset : 0,
            },
            guideNotConfirmedToSeller: {
                enabled: values.seller,
                offset: values.seller ? values.sellerOffset : 0,
            },
        };
        try {
            await updateEmailReminders(data);
            flash.success("Your settings are saved");
        } catch {}

        invalidate("sellers", seller.id);
    };

    return (
        <ContentLayout isWide className="space-y-6">
            <XolaFormik
                validateOnChange
                validationSchema={validationSchema}
                initialValues={initialState}
                onSubmit={handleSave}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, errors, isValid }) => {
                    return (
                        <>
                            <ConfirmLeaveModal isFormTouched={!isEqual(initialState, values)} />
                            <Switch.Group>
                                <Switch
                                    isChecked={values.open}
                                    size="medium"
                                    data-testid="global-settings-email-tab-guide-assignment-confirmation-toggle"
                                    onChange={(value: boolean) => {
                                        setFieldValue("open", value);
                                    }}
                                />
                                <Switch.Label direction="right">
                                    <h4>Require guide assignment confirmation</h4>
                                </Switch.Label>
                            </Switch.Group>
                            {values.open ? (
                                <div>
                                    <Switch.Group
                                        className="ml-5"
                                        data-testid="global-settings-email-tab-allow-decline-row"
                                    >
                                        <Switch
                                            isChecked={values.allowDecline}
                                            size="medium"
                                            data-testid="global-settings-email-tab-allow-decline-toggle"
                                            onChange={(value: boolean) => setFieldValue("allowDecline", value)}
                                        />
                                        <div className="ml-5">Allow guides to decline their assignment</div>
                                    </Switch.Group>

                                    <br />
                                    <Switch.Group className="mt-5 ml-5">
                                        <Switch
                                            isChecked={values.guide}
                                            size="medium"
                                            data-testid="global-settings-email-tab-remind-upcoming-assignment-toggle"
                                            onChange={(value: boolean) => setFieldValue("guide", value)}
                                        />
                                        <div
                                            className="ml-5"
                                            data-testid="global-settings-email-tab-remind-upcoming-assignment-msg"
                                        >
                                            Remind my guide about their upcoming assignment if they have not confirmed
                                            assignment
                                            <TimePickerPopover
                                                value={values?.offset ?? 0}
                                                disabled={!values.guide}
                                                isError={!!errors.offset}
                                                onApply={(value) => {
                                                    setFieldValue("offset", value);
                                                }}
                                            />
                                            before event start time
                                        </div>
                                    </Switch.Group>

                                    <br />
                                    <Switch.Group className="mt-5 ml-5">
                                        <Switch
                                            isChecked={values.seller}
                                            size="medium"
                                            data-testid="global-settings-email-tab-remind-has-not-acknowledged-their-assignment-toggle"
                                            onChange={(value: boolean) => setFieldValue("seller", value)}
                                        />
                                        <div
                                            className="ml-5"
                                            data-testid="global-settings-email-tab-remind-has-not-acknowledged-their-assignment-msg"
                                        >
                                            Notify me if a guide has not acknowledged their assignment
                                            <TimePickerPopover
                                                value={values?.sellerOffset ?? 0}
                                                disabled={!values.seller}
                                                isError={!!errors.sellerOffset}
                                                onApply={(value) => {
                                                    setFieldValue("sellerOffset", value);
                                                }}
                                            />
                                            before event start time
                                        </div>
                                    </Switch.Group>
                                </div>
                            ) : null}

                            <ContentLayout.Footer>
                                <SubmitButton disabled={!isValid} isLoading={isLoading} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                                <SecondaryButton type="reset" disabled={isLoading} onClick={handleReset}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const tabIds = [{ id: "permissions" }, { id: "email" }];
